
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, CreditCard, Calendar, AlertCircle, ExternalLink } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import PortalLayout from "@/components/portal/PortalLayout";
import { supabaseGet } from "@/utils/supabaseRest";
import { useAuth } from "@/contexts/AuthContext";

interface Plan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  features: string[];
  max_chatbots: number;
  stripe_price_id: string;
  is_active: boolean;
}

interface UserProfile {
  id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string;
  plan_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
}

interface Subscription {
  id: string;
  plan_id: string;
  status: string;
  cancel_at_period_end: boolean;
}

const PortalBillingPlan = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const { data: plans } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      return await supabaseGet<Plan[]>("plans?select=*&is_active=eq.true&order=price.asc");
    },
  });

  const { data: profile } = useQuery({
    queryKey: ["userProfile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const rows = await supabaseGet<UserProfile[]>(`profiles?select=*&id=eq.${user.id}`);
      return rows.length > 0 ? rows[0] : null;
    },
    enabled: !!user?.id,
  });

  const { data: subscription } = useQuery({
    queryKey: ["subscription", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const rows = await supabaseGet<Subscription[]>(`subscriptions?select=*&user_id=eq.${user.id}&status=eq.active`);
      return rows.length > 0 ? rows[0] : null;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (profile) {
      setUserProfile(profile);
      setCurrentPlanId(profile.plan_id);
    }
  }, [profile]);

  // Mutation for accessing billing portal
  const billingPortalMutation = useMutation({
    mutationFn: async () => {
      const { createBillingPortalSession } = await import('@/api/stripe-billing-portal');
      return await createBillingPortalSession();
    },
    onSuccess: (data) => {
      toast({
        title: 'Redirecting to Billing Portal',
        description: 'Opening Stripe billing portal...',
      });

      // Redirect to actual Stripe billing portal
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to get billing portal URL. Please try again.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to access billing portal',
        variant: 'destructive',
      });
    },
  });

  // Mutation for plan changes
  const planChangeMutation = useMutation({
    mutationFn: async (planId: string) => {
      const { createCheckoutSession } = await import('@/api/stripe');
      return await createCheckoutSession(planId);
    },
    onSuccess: (data) => {
      toast({
        title: 'Plan Change Initiated',
        description: `Changing to ${data.planName} plan. Redirecting to checkout...`,
      });

      // Redirect to actual Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to change plan',
        variant: 'destructive',
      });
    },
  });

  const handleSelectPlan = async (planId: string) => {
    if (planId === currentPlanId) return;

    planChangeMutation.mutate(planId);
  };

  const handleBillingPortal = () => {
    billingPortalMutation.mutate();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600';
      case 'past_due':
        return 'text-yellow-600';
      case 'canceled':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getCurrentPlan = () => {
    if (!currentPlanId || !plans) return null;
    return plans.find(plan => plan.id === currentPlanId);
  };

  const currentPlan = getCurrentPlan();

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Billing & Subscription</h1>

          {/* Current Subscription Status */}
          {userProfile && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Current Subscription
                </CardTitle>
                <CardDescription>
                  Manage your subscription and billing information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Current Plan</p>
                    <p className="text-lg font-semibold">
                      {currentPlan?.name || 'No Plan'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <p className={`text-lg font-semibold capitalize ${getSubscriptionStatusColor(userProfile.subscription_status)}`}>
                      {userProfile.subscription_status}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Current Period</p>
                    <p className="text-sm">
                      {formatDate(userProfile.current_period_start)} - {formatDate(userProfile.current_period_end)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Monthly Cost</p>
                    <p className="text-lg font-semibold">
                      ${currentPlan?.price || 0}/month
                    </p>
                  </div>
                </div>

                {/* Billing Portal Access */}
                {userProfile.stripe_customer_id && (
                  <div className="mt-6 pt-6 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium">Manage Billing</h3>
                        <p className="text-sm text-gray-600">
                          Update payment methods, view invoices, and manage your subscription
                        </p>
                      </div>
                      <Button
                        onClick={handleBillingPortal}
                        disabled={billingPortalMutation.isPending}
                        className="flex items-center gap-2"
                        data-testid="billing-portal-button"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {billingPortalMutation.isPending ? 'Loading...' : 'Billing Portal'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Plan Change Alert */}
          {userProfile?.subscription_status === 'past_due' && (
            <Alert className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your subscription payment is past due. Please update your payment method to continue using the service.
              </AlertDescription>
            </Alert>
          )}

          {/* Available Plans */}
          <Card>
            <CardHeader>
              <CardTitle>Available Plans</CardTitle>
              <CardDescription>
                Choose the plan that best fits your needs. You can change your plan at any time.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-3">
                {plans?.map((plan) => (
                  <Card
                    key={plan.id}
                    className={`p-6 ${plan.id === currentPlanId ? 'border-primary ring-2 ring-primary/20' : ''}`}
                    data-testid={`plan-card-${plan.id}`}
                  >
                    {plan.id === currentPlanId && (
                      <div className="bg-primary/10 text-primary text-sm font-medium px-3 py-1 rounded-full w-fit mb-4">
                        Current Plan
                      </div>
                    )}
                    <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                    <p className="text-3xl font-bold mb-4">
                      ${plan.price}<span className="text-sm font-normal text-gray-600">/mo</span>
                    </p>
                    {plan.description && (
                      <p className="text-gray-600 mb-4">{plan.description}</p>
                    )}
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-primary" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={plan.id === currentPlanId ? "default" : "outline"}
                      className="w-full"
                      onClick={() => handleSelectPlan(plan.id)}
                      disabled={plan.id === currentPlanId || planChangeMutation.isPending}
                      data-testid={`select-plan-${plan.id}`}
                    >
                      {planChangeMutation.isPending ? 'Processing...' :
                       plan.id === currentPlanId ? "Current Plan" :
                       plan.price > (currentPlan?.price || 0) ? "Upgrade" : "Downgrade"}
                    </Button>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalBillingPlan;
